import { useState, useEffect, useCallback } from 'react';
import { HTMLTemplateModel } from '../../../models/html.template.model';
import { DocumentTemplateModel } from '../../../models/document.template.model';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../common/hooks/useAppSelector';
import { selectUser } from '../../../slices/user/userSlice';
import { selectAllDocuments } from '../../../slices/documents/documentsSlice';
import { useAppDispatch } from '../../../common/hooks/useAppDispatch';
import { NWClient } from '../../../client/NWClient';
import { toast } from 'react-toastify';
import { fetchDisclaimerInfo } from '../../../slices/disclaimer/disclaimerSlice';
import { TAGS_LABELS } from '../../../common/constants/tags-labels';
import { Helpers } from '../../../common/helpers/helpers';

export const useLibraryItemDetails = () => {
  const { id } = useParams();
  const { name: templateCategory } = useParams();
  const [params] = useSearchParams();
  const currentUser = useAppSelector(selectUser);
  const generateDocumentParam = params.get('generateDocument');
  const { t } = useTranslation();
  const [dlHTML, setDLHTML] = useState<string>('');
  const [loadingHTML, setLoadingHTML] = useState<boolean>(true);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [documentTypeLabel, setDocumentTypeLabel] = useState<string>('');
  const [documentWorkflowLabel, setDocumentWorkflowLabel] = useState<string>('');
  const [propertiesEditMode, setPropertiesEditMode] = useState<boolean>(false);
  const [contractCreationEnabled, setContractCreationEnabled] = useState<boolean>(false);
  const [customLabel, setCustomLabel] = useState<string>('');
  const documents = useAppSelector(selectAllDocuments).filter((el) => el.user === currentUser.id);
  const [templateItem, setTemplateItem] = useState<HTMLTemplateModel | DocumentTemplateModel>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const categoryArray = ['Business', 'Customers', 'Employment', 'Suppliers'];

  const categorySelectValues = categoryArray.map((category) => ({
    value: category,
    label: category,
  }));

  const documentWorkflowLabelsEmployment = TAGS_LABELS.documentWorkflow.employment.map((el) =>
    t(el)
  );
  const documentWorkflowLabelsEmploymentSorted = documentWorkflowLabelsEmployment.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsSupplier = TAGS_LABELS.documentWorkflow.supplier.map((el) => t(el));
  const documentWorkflowLabelsSupplierSorted = documentWorkflowLabelsSupplier.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsCustomer = TAGS_LABELS.documentWorkflow.customer.map((el) => t(el));
  const documentWorkflowLabelsCustomerSorted = documentWorkflowLabelsCustomer.sort((a, b) =>
    a.localeCompare(b)
  );
  const documentWorkflowLabelsBusiness = TAGS_LABELS.documentWorkflow.business.map((el) => t(el));
  const documentWorkflowLabelsBusinessSorted = documentWorkflowLabelsBusiness.sort((a, b) =>
    a.localeCompare(b)
  );

  const workflowsArray = () => {
    // Use the current category state rather than documentDetails.category
    const currentCategory = category
      ? category.toLowerCase()
      : templateItem?.category
        ? templateItem.category
        : '';

    switch (currentCategory) {
      case 'employment':
        return documentWorkflowLabelsEmploymentSorted.map((el) => ({ label: el, value: el }));
      case 'suppliers':
        return documentWorkflowLabelsSupplierSorted.map((el) => ({ label: el, value: el }));
      case 'customers':
        return documentWorkflowLabelsCustomerSorted.map((el) => ({ label: el, value: el }));
      case 'business':
        return documentWorkflowLabelsBusinessSorted.map((el) => ({ label: el, value: el }));
      default:
        return [];
    }
  };

  const documentTypeLabels = TAGS_LABELS.documentType.map((el) => t(el));
  const getTypeLabels = () => {
    return documentTypeLabels.map((el) => ({ label: el, value: el }));
  };

  const templateTitle = templateItem
    ? templateItem.name
      ? templateItem.name.replace('.pdf', '').replace('.doc', '').replace('.docx', '')
      : 'document_upload' in templateItem &&
          templateItem.document_upload &&
          'updated' in templateItem &&
          documents.length > 0
        ? documents.find((el) => el.id === templateItem.document_upload).name +
          '_' +
          templateItem.updated
        : t('templatePage.headerDefault')
    : t('templatePage.headerDefault');

  const getTemplateHTML = useCallback(
    () => {
      if (id && templateCategory) {
        NWClient.get(
          `${templateCategory === 'company-templates' ? 'document-template' : 'document-library'}`,
          Number(id)
        )
          .then((res: HTMLTemplateModel) => {
            setTemplateItem(res);
            if (templateCategory !== 'company-templates') {
              NWClient.get('document-library-html', Number(id))
                .then((res: { id: number; html: string }) => {
                  setDLHTML(res.html);
                })
                .catch((error) => {
                  toast.error(error.message ? error.message : t('messages.errorOccurred'));
                })
                .finally(() => {
                  setLoadingHTML(false);
                });
            } else {
              setDLHTML(res.content);
            }
          })
          .catch((error) => {
            toast.error(error.message ? error.message : t('messages.errorOccurred'));
          })
          .finally(() => {
            templateCategory === 'company-templates' && setLoadingHTML(false);
          });
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, templateCategory]
  );

  const updateLibraryItem = () => {
    setLoading(true);
    NWClient.patch(`document-template/${templateItem?.id}/`, null, {
      user: currentUser.id,
      name: templateName ? templateName : t('labels.templateDefault'),
      category: category,
    })
      .then(() => {
        NWClient.patch('tag-document-template', templateItem?.id, {
          user: currentUser.id,
          dt_workflows: documentWorkflowLabel ? [documentWorkflowLabel] : [],
          dt_types: documentTypeLabel ? [documentTypeLabel] : [],
          dt_label: customLabel ? [customLabel] : [],
        })
          .then(() => {
            toast.success(t('messages.templateUpdated'));
            NWClient.get('document-template', Number(id)).then((res: HTMLTemplateModel) => {
              setTemplateItem(res);
            });
          })
          .finally(() => {
            setLoading(false);
            setPropertiesEditMode(false);
          });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleSave = () => {
    setLoading(true);
    const data: { user: number; name: string; content?: string; html?: string } = {
      user: null,
      name: null,
    };
    data.user = currentUser.id;
    data.name = templateItem.name;
    templateCategory === 'company-templates'
      ? 'content' in templateItem && (data.content = dlHTML)
      : 'html' in templateItem && (data.html = dlHTML);
    NWClient.patch(
      `${templateCategory === 'company-templates' ? 'document-template' : 'document-library'}/${
        templateItem?.id
      }/`,
      null,
      data
    )
      .then(() => {
        toast.success(t('messages.templateUpdated'));
        setEditMode(false);
      })
      .catch((error) => {
        toast.error(error.message || t('messages.errorOccurred'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (templateItem && 'document_upload' in templateItem) {
      setTemplateName(templateItem?.name);
      setCategory(
        templateItem?.category ? Helpers.capitalizeFirstLetter(templateItem?.category) : ''
      );
      setCustomLabel(
        templateItem?.dt_label && templateItem?.dt_label.length > 0 ? templateItem?.dt_label[0] : ''
      );
      setDocumentWorkflowLabel(
        templateItem?.dt_workflows && templateItem?.dt_workflows.length > 0
          ? templateItem?.dt_workflows[0]
          : ''
      );
      setDocumentTypeLabel(
        templateItem?.dt_types && templateItem?.dt_types.length > 0 ? templateItem?.dt_types[0] : ''
      );
    }
  }, [templateItem]);

  useEffect(() => {
    dispatch(fetchDisclaimerInfo());
  }, [dispatch]);

  useEffect(() => {
    getTemplateHTML();
  }, [getTemplateHTML]);

  useEffect(() => {
    if (generateDocumentParam) {
      setContractCreationEnabled(true);
    }
  }, [generateDocumentParam]);

  return {
    loading,
    dlHTML,
    setDLHTML,
    templateTitle,
    editMode,
    setEditMode,
    loadingHTML,
    setLoadingHTML,
    contractCreationEnabled,
    setContractCreationEnabled,
    templateCategory,
    templateItem,
    handleSave,
    setPropertiesEditMode,
    documentTypeLabel,
    setDocumentTypeLabel,
    documentWorkflowLabel,
    setDocumentWorkflowLabel,
    propertiesEditMode,
    category,
    setCategory,
    categorySelectValues,
    getTypeLabels,
    templateName,
    setTemplateName,
    setCustomLabel,
    customLabel,
    workflowsArray,
    updateLibraryItem,
  };
};
